/**
 * Utility for managing upload progress tracking via localStorage
 * Stores document IDs that are being tracked for processing status
 */

const STORAGE_KEY = 'upload_progress_docs';
const CLEANUP_DELAY_KEY = 'upload_progress_cleanup_time';
const CLEANUP_DELAY_MS = 60 * 1000; // 1 minute

export interface UploadProgressDoc {
  documentId: string;
  uploadedAt: number;
}

/**
 * Add document IDs to the upload progress tracking list
 */
export function addUploadProgressDocs(documentIds: string[]): void {
  try {
    const existing = getUploadProgressDocs();
    const existingIds = new Set(existing.map(d => d.documentId));
    
    const newDocs: UploadProgressDoc[] = documentIds
      .filter(id => !existingIds.has(id))
      .map(id => ({
        documentId: id,
        uploadedAt: Date.now(),
      }));
    
    const updated = [...existing, ...newDocs];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    
    // Extend cleanup time when new files are added (reschedule for 1 minute from now)
    scheduleUploadProgressCleanup();
  } catch (e) {
    console.error('Error adding upload progress docs:', e);
  }
}

/**
 * Get all document IDs being tracked for upload progress
 */
export function getUploadProgressDocs(): UploadProgressDoc[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    
    const docs: UploadProgressDoc[] = JSON.parse(stored);
    
    // Filter out docs older than 24 hours (stale tracking)
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    return docs.filter(d => d.uploadedAt > oneDayAgo);
  } catch (e) {
    console.error('Error getting upload progress docs:', e);
    return [];
  }
}

/**
 * Get just the document IDs array
 */
export function getUploadProgressDocIds(): string[] {
  return getUploadProgressDocs().map(d => d.documentId);
}

/**
 * Remove specific document IDs from tracking
 */
export function removeUploadProgressDocs(documentIds: string[]): void {
  try {
    const existing = getUploadProgressDocs();
    const idsToRemove = new Set(documentIds);
    const updated = existing.filter(d => !idsToRemove.has(d.documentId));
    
    if (updated.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  } catch (e) {
    console.error('Error removing upload progress docs:', e);
  }
}

/**
 * Clear all upload progress tracking
 */
export function clearUploadProgressDocs(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CLEANUP_DELAY_KEY);
  } catch (e) {
    console.error('Error clearing upload progress docs:', e);
  }
}

/**
 * Schedule cleanup of upload progress docs after a delay
 * This allows new files to be added to the same batch within the delay window
 */
export function scheduleUploadProgressCleanup(): void {
  try {
    const cleanupTime = Date.now() + CLEANUP_DELAY_MS;
    localStorage.setItem(CLEANUP_DELAY_KEY, cleanupTime.toString());
  } catch (e) {
    console.error('Error scheduling cleanup:', e);
  }
}

/**
 * Check if cleanup time has passed and clear if needed
 * Returns true if cleanup was performed
 */
export function checkAndCleanupIfExpired(): boolean {
  try {
    const cleanupTimeStr = localStorage.getItem(CLEANUP_DELAY_KEY);
    if (!cleanupTimeStr) return false;
    
    const cleanupTime = parseInt(cleanupTimeStr, 10);
    if (Date.now() >= cleanupTime) {
      clearUploadProgressDocs();
      return true;
    }
    return false;
  } catch (e) {
    console.error('Error checking cleanup:', e);
    return false;
  }
}

/**
 * Cancel scheduled cleanup (e.g., when new files are added)
 */
export function cancelScheduledCleanup(): void {
  try {
    localStorage.removeItem(CLEANUP_DELAY_KEY);
  } catch (e) {
    console.error('Error canceling cleanup:', e);
  }
}

/**
 * Check if there are any documents being tracked
 */
export function hasUploadProgressDocs(): boolean {
  return getUploadProgressDocs().length > 0;
}
